/**
 * Communication Trace dashboard — view-model types (ODE-P10-T07).
 *
 * Thin client-side mirror of the ODE-P10-T06 read-model contracts
 * (apps/ode-api/src/communication-trace/types.ts). The dashboard is a
 * view-model only: every value rendered here comes from the T06 versioned
 * read endpoints; no aggregation, scoring, or verdict is computed client-side
 * (REQ-098/110). Coverage indicators are rendered strictly from the T06
 * coverage/permission state — a missing capability is a visible gap, never
 * fabricated data (REQ-109 visibility).
 *
 * Traces to: ODE-PRD-REQ-098, ODE-PRD-REQ-099, ODE-PRD-REQ-100,
 *            ODE-PRD-REQ-101, ODE-PRD-REQ-110.
 */

/** Approved purpose codes for sensitive trace access/export (REQ-101). */
export const APPROVED_PURPOSE_CODES = [
  'investigation',
  'audit',
  'compliance',
  'security_incident',
] as const;
export type PurposeCode = (typeof APPROVED_PURPOSE_CODES)[number];

export const CASE_REF_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

export type WindowLabel = '7d' | '30d' | '90d' | 'custom';

/** Explicit aggregation window definition (T06 ObservationWindow). */
export interface ObservationWindow {
  readonly kind: 'day' | 'week' | 'month' | 'custom';
  readonly start: string;
  readonly end: string;
  readonly label: string;
}

/** Source coverage / permission state record (T06 SourceCoverageRecord). */
export interface SourceCoverageRecord {
  readonly sourceKind: 'CONTACT_SNAPSHOT' | 'CALL_METADATA' | 'WHATSAPP_NOTIFICATION';
  readonly status: string;
  readonly lastSeenAt: string | null;
  readonly observedCount: number;
  readonly permissionState: 'GRANTED' | 'REVOKED' | 'NOT_REQUIRED' | 'POLICY_GATED' | 'UNKNOWN';
}

export type CoverageOverall = 'FULL' | 'PARTIAL' | 'NONE' | 'UNKNOWN';

export interface CoverageSummary {
  readonly sources: readonly SourceCoverageRecord[];
  readonly overall: CoverageOverall;
}

/** Contact frequency entry (T06 ContactFrequencyEntry). */
export interface ContactFrequencyEntry {
  readonly contactKey: string;
  readonly displayName: string | null;
  readonly sourceContactIds: readonly string[];
  readonly identityAmbiguity: boolean;
  readonly observationCount: number;
  readonly firstObservedAt: string | null;
  readonly lastObservedAt: string | null;
  readonly provenance: {
    readonly sourceKind: 'CONTACT_SNAPSHOT' | 'CALL_METADATA' | 'WHATSAPP_NOTIFICATION';
    readonly contributingEventIds: readonly string[];
    readonly coverage: SourceCoverageRecord;
    readonly note: string;
  };
}

export interface CallDirectionCount {
  readonly direction: 'INCOMING' | 'OUTGOING' | 'MISSED' | 'OTHER';
  readonly count: number;
}

export interface CallSummary {
  readonly totalCount: number;
  readonly byDirection: readonly CallDirectionCount[];
  readonly duration: { readonly totalSeconds: number; readonly medianSeconds: number | null; readonly sampleCount: number };
  readonly firstObservedAt: string | null;
  readonly lastObservedAt: string | null;
  readonly provenance: {
    readonly sourceKind: 'CONTACT_SNAPSHOT' | 'CALL_METADATA' | 'WHATSAPP_NOTIFICATION';
    readonly contributingEventIds: readonly string[];
    readonly coverage: SourceCoverageRecord;
    readonly note: string;
  };
}

export interface WhatsAppActivityEntry {
  readonly day: string;
  readonly notificationCount: number;
}

export interface WhatsAppSummary {
  readonly totalNotificationCount: number;
  readonly activityByDay: readonly WhatsAppActivityEntry[];
  readonly firstObservedAt: string | null;
  readonly lastObservedAt: string | null;
  readonly provenance: {
    readonly sourceKind: 'CONTACT_SNAPSHOT' | 'CALL_METADATA' | 'WHATSAPP_NOTIFICATION';
    readonly contributingEventIds: readonly string[];
    readonly coverage: SourceCoverageRecord;
    readonly note: string;
  };
}

/** Read-model summary envelope (T06 CommunicationTraceSummary). */
export interface CommunicationTraceSummary {
  readonly driverId: string;
  readonly window: ObservationWindow;
  readonly generatedAt: string;
  readonly contacts: readonly ContactFrequencyEntry[];
  readonly calls: CallSummary;
  readonly whatsapp: WhatsAppSummary;
  readonly coverage: CoverageSummary;
  /** REQ-110: investigative leads only — no criminality determination. */
  readonly analyticsBoundary: string;
  readonly schemaVersion: number;
}

/** Contact detail read model (T06 ContactDetail). */
export interface ContactDetail {
  readonly driverId: string;
  readonly window: ObservationWindow;
  readonly contact: ContactFrequencyEntry;
  readonly relatedCalls: CallSummary;
  readonly generatedAt: string;
  readonly analyticsBoundary: string;
  readonly schemaVersion: number;
}

/** Search result entry (T06 SearchResultEntry). */
export interface SearchResultEntry {
  readonly contactKey: string;
  readonly displayName: string | null;
  readonly identityAmbiguity: boolean;
  readonly observationCount: number;
  readonly lastObservedAt: string | null;
}

export interface SearchResults {
  readonly driverId: string;
  readonly query: string;
  readonly window: ObservationWindow;
  readonly results: readonly SearchResultEntry[];
  readonly resultCount: number;
  readonly truncated: boolean;
  readonly generatedAt: string;
  readonly analyticsBoundary: string;
  readonly schemaVersion: number;
}

/** Export bundle (T06 ExportBundle). */
export interface ExportBundle {
  readonly driverId: string;
  readonly window: ObservationWindow;
  readonly summary: CommunicationTraceSummary;
  readonly generatedAt: string;
  readonly exportedBy: string;
  readonly purposeCode: string;
  readonly caseRef: string | null;
  readonly analyticsBoundary: string;
  readonly schemaVersion: number;
}

/** REQ-090-style state model for the summary flow. */
export type TraceSummaryState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly summary: CommunicationTraceSummary }
  | { readonly kind: 'empty'; readonly summary: CommunicationTraceSummary }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'unavailable'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

/** State model for gated actions (detail / search / export). */
export type TraceActionState<T> =
  | { readonly kind: 'idle' }
  | { readonly kind: 'gated' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly payload: T }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'unavailable'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

/** Client-side view filters over the T06 summary payload (thin view-model). */
export interface TraceViewFilters {
  /** Maps to the T06 `window` query param. */
  readonly window: WindowLabel;
  /** Custom window start (ISO date) when window === 'custom'. */
  readonly start: string;
  /** Custom window end (ISO date) when window === 'custom'. */
  readonly end: string;
  /** Client-side filter over contacts by provenance source kind. */
  readonly source: 'ALL' | 'CONTACT_SNAPSHOT' | 'CALL_METADATA' | 'WHATSAPP_NOTIFICATION';
  /** Client-side filter over call direction counts. */
  readonly direction: 'ALL' | 'INCOMING' | 'OUTGOING' | 'MISSED' | 'OTHER';
  /** Client-side text filter over contact display name / key. */
  readonly contact: string;
}

export const DEFAULT_TRACE_FILTERS: TraceViewFilters = {
  window: '7d',
  start: '',
  end: '',
  source: 'ALL',
  direction: 'ALL',
  contact: '',
};